import React from "react";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import {
  Formik,
  Form,
  Field,
  FieldProps,
  ErrorMessage,
  FormikActions,
} from "formik";
import {
  Box,
  Debug,
  Flex,
  Text,
  Heading,
  Label,
  Input,
  SelectStyled,
  useFetch,
} from "@pacerpro/shared-ui";
import { Offices } from "@pacerpro/api";
import { computedOfficeValue } from "@pacerpro/utils";
import { setOffices } from "../../reducers/offices";
import { editAdmin } from "../../reducers/organizations";
import { selectOffices, selectSelectedAdmin } from "../../selectors";
import SelectAdmin from "./SelectAdmin";

const { NODE_ENV } = process.env;

const regex = {
  notDefault: /^((?!^default$).)*$/,
  isEmail: /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
};

interface FormValues {
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  office?: string;
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email()
    .matches(regex.isEmail, "email must be a valid email")
    .required("Email is required."),
  firstName: Yup.string()
    .trim()
    .required("First name is required."),
  lastName: Yup.string()
    .trim()
    .required("Last name is required."),
  office: Yup.string().matches(regex.notDefault, "Select an office."),
});

const AdminForm: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const [officesResponse, officesLoading] = useFetch(Offices.getAll);
  const offices = useSelector(selectOffices);
  const selectedAdmin = useSelector(selectSelectedAdmin);

  React.useEffect(() => {
    if (!officesLoading && officesResponse) {
      dispatch(setOffices(officesResponse));
    }
  }, [officesResponse, officesLoading]);

  const adminHeader = (values: FormValues) =>
    selectedAdmin
      ? `${values.firstName} ${values.lastName}`
      : "New Administrator";

  const initialValues: FormValues = {
    email: selectedAdmin ? selectedAdmin.attributes.email : "",
    password: selectedAdmin ? selectedAdmin.attributes.password : "",
    firstName: selectedAdmin ? selectedAdmin.attributes.firstName : "",
    lastName: selectedAdmin ? selectedAdmin.attributes.lastName : "",
    office: selectedAdmin
      ? computedOfficeValue(selectedAdmin.attributes.office, offices)
      : "",
  };

  const onSubmit = async (
    values: FormValues,
    actions: FormikActions<FormValues>,
  ) => {
    if (selectedAdmin) {
      const attributes = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        office: values.office,
      };
      await dispatch(editAdmin(selectedAdmin.id, attributes));
    }
    actions.setSubmitting(false);
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    field: FieldProps["field"],
  ) => {
    field.onBlur(e);
    if (selectedAdmin) {
      const { name, value } = e.currentTarget;
      dispatch(
        editAdmin(selectedAdmin.id, {
          [name]: value,
        }),
      );
    }
  };

  const handleChange = async (
    e: { value: number | string; name: string },
    field: FieldProps["field"],
  ) => {
    const event = {
      target: {
        name: field.name,
        value: e.value,
      },
      currentTarget: {
        name: field.name,
        value: e.value,
      },
    };
    if (selectedAdmin) {
      try {
        await dispatch(
          editAdmin(selectedAdmin.id, {
            [field.name]: e.value,
          }),
        );
        field.onChange(event);
      } catch (error) {
        console.error(error);
      }
    } else {
      field.onChange(event);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {formikProps => {
        return (
          <Form autoComplete="new-password">
            <Flex
              align="center"
              mx={8}
              my={7}
              pb={3}
              sx={{ borderBottom: "1px solid", borderColor: "border" }}
            >
              <Heading fontWeight="medium">
                {adminHeader(formikProps.values)}
              </Heading>
            </Flex>
            <Flex ml={8} mr={8} direction="column">
              <Box mb={4}>
                {selectedAdmin ? (
                  <Field
                    name="email"
                    render={({ field }: FieldProps) => (
                      <>
                        <Label htmlFor="email" mb={1}>
                          Email
                        </Label>
                        <Input
                          id="email"
                          type="search"
                          placeholder="john@example.com"
                          {...field}
                          autoComplete="new-password"
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                            handleBlur(e, field)
                          }
                        />
                        <ErrorMessage
                          component={Text}
                          name="email"
                          sx={{ color: "red" }}
                        />
                      </>
                    )}
                  />
                ) : (
                  <Field
                    name="email"
                    render={({ field, form }: FieldProps) => (
                      <SelectAdmin field={field} form={form} />
                    )}
                  />
                )}
              </Box>
              <Box mb={4}>
                <Field
                  name="password"
                  render={({ field }: FieldProps) => (
                    <>
                      <Label htmlFor="password" mb={1}>
                        Password
                      </Label>
                      <Input
                        id="password"
                        type="search"
                        {...field}
                        autoComplete="new-password"
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                          handleBlur(e, field)
                        }
                      />
                      <ErrorMessage
                        component={Text}
                        name="password"
                        sx={{ color: "red" }}
                      />
                    </>
                  )}
                />
              </Box>
              <Flex mb={4} align="flex-start">
                <Box mr={2} width="100%">
                  <Field
                    name="firstName"
                    render={({ field }: FieldProps) => (
                      <>
                        <Label htmlFor="firstName" mb={1}>
                          Name
                        </Label>
                        <Input
                          id="firstName"
                          type="text"
                          placeholder="First"
                          {...field}
                          autoComplete="new-password"
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                            handleBlur(e, field)
                          }
                        />
                        <ErrorMessage
                          component={Text}
                          name="firstName"
                          sx={{ color: "red" }}
                        />
                      </>
                    )}
                  />
                </Box>
                <Box ml={2} width="100%">
                  <Field
                    name="lastName"
                    render={({ field }: FieldProps) => (
                      <>
                        <Label htmlFor="firstName" mb={1}>
                          Last
                        </Label>
                        <Input
                          id="lastName"
                          type="text"
                          placeholder="Last"
                          {...field}
                          autoComplete="new-password"
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                            handleBlur(e, field)
                          }
                        />
                        <ErrorMessage
                          component={Text}
                          name="lastName"
                          sx={{ color: "red" }}
                        />
                      </>
                    )}
                  />
                </Box>
              </Flex>
              <Box mb={4} width="100%">
                <Field
                  name="office"
                  render={({ field }: FieldProps) => (
                    <>
                      <Label htmlFor="office" mb={1}>
                        Office
                      </Label>
                      <SelectStyled
                        id="office"
                        {...field}
                        onChange={(e: any) => handleChange(e, field)}
                        options={offices.map(office => {
                          return {
                            value: office.id,
                            label: office.attributes.name,
                          };
                        })}
                        defaultValue={null}
                        variant="primary"
                      ></SelectStyled>
                      <ErrorMessage
                        component={Text}
                        name="office"
                        sx={{ color: "red" }}
                      />
                    </>
                  )}
                />
              </Box>
            </Flex>
            {NODE_ENV === "development" && (
              <Debug sx={{ m: 8 }} label="Form State" state={formikProps} />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default AdminForm;
